import { useState, useMemo } from "react";
import { useAuth } from '../../../provider'
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Grid from '@mui/material/Grid';
import { Box, Typography, Tooltip, IconButton, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ButtonForm from "../../../components/Button/ButtonForm";
import * as c from '../../../constants';
import axios from 'axios';
import PasswordInput from '../../../components/Forms/PasswordInput';
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "../../../language";
import { Wand2, Copy, Check, ArrowDownToLine } from 'lucide-react';

const passwordRegExp =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()[\]{};:'",.<>?\\|`~\-_=+]).{8,}$/;

const createValidationSchema = (t) => Yup.object({
  password: Yup.string()
    .matches(passwordRegExp, t('val_pw_strong'))
    .required(t('val_pw_req')),
  password2: Yup.string()
    .oneOf([Yup.ref('password'), null], t('val_pw2_match'))
    .required(t('val_pw2_req')),
  old_password: Yup.string()
    .required(t('val_old_pw_req')),
});

function generatePassword() {
  const upper   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower   = 'abcdefghijklmnopqrstuvwxyz';
  const digits  = '0123456789';
  const special = '!@#$%^&*-_=+';
  const all = upper + lower + digits + special;

  const required = [
    upper  [Math.floor(Math.random() * upper.length)],
    lower  [Math.floor(Math.random() * lower.length)],
    digits [Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];
  const rest = Array.from({ length: 12 }, () => all[Math.floor(Math.random() * all.length)]);
  return [...required, ...rest].sort(() => Math.random() - 0.5).join('');
}

const PwChangeForm = () => {

  const theme = useTheme();
  const { t, language } = useTranslation();
  const validationSchema = useMemo(() => createValidationSchema(t), [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const { state } = useAuth();
  const url_pwchange = `${c.API_URL}/api/auth/change_password/${state.user.id}/`;

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showPassword3, setShowPassword3] = useState(false);

  const [message, setMessage] = useState();
  const [errmessage, setErrmessage] = useState();
  const [generatedPw, setGeneratedPw] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (values) => {
    setMessage();
    setErrmessage();
    try {
      const response = await axios.put(url_pwchange, values);
      setMessage(response);
    } catch (error) {
      setErrmessage(error);
    }
  };

  const handleGenerate = () => {
    setGeneratedPw(generatePassword());
    setCopied(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPw);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isDark = theme.palette.mode === 'dark';
  const cardSx = {
    p: 4,
    borderRadius: 3,
    overflow: 'hidden',
    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  };

  return (
    <Formik
      initialValues={{ password: '', password2: '', old_password: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await handleSubmit(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form>

          <Grid container sx={cardSx}>

            {/* Password generator widget */}
            <Grid size={12}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, mb: 1,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400',
              }}>
                <Tooltip title={t('ua_pw_gen_suggest')} placement="top">
                  <IconButton size="small" onClick={handleGenerate} sx={{ flexShrink: 0 }}>
                    <Wand2 size={16} color={theme.palette.quartiary.C300} />
                  </IconButton>
                </Tooltip>

                {generatedPw ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ flex: 1, fontFamily: 'monospace', letterSpacing: 1, wordBreak: 'break-all' }}
                    >
                      {generatedPw}
                    </Typography>

                    <Tooltip title={copied ? t('ua_pw_gen_copied') : t('ua_pw_gen_copy')} placement="top">
                      <IconButton size="small" onClick={handleCopy}>
                        {copied
                          ? <Check size={16} color={theme.palette.traffic.green} />
                          : <Copy size={16} color={theme.palette.text.secondary} />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('ua_pw_gen_apply')} placement="top">
                      <IconButton size="small" onClick={() => {
                        setFieldValue('password', generatedPw);
                        setFieldValue('password2', generatedPw);
                      }}>
                        <ArrowDownToLine size={16} color={theme.palette.text.secondary} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('ua_pw_gen_suggest')}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid size={12}>
              <PasswordInput
                label={t('ua_pw_new')}
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder={t('ua_pw_new')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={12}>
              <PasswordInput
                label={t('ua_pw_new_repeat')}
                name="password2"
                type={showPassword2 ? "text" : "password"}
                placeholder={t('ua_pw_new_repeat')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword2(p => !p)} edge="end">
                        {showPassword2 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={12}>
              <PasswordInput
                label={t('ua_pw_old')}
                name="old_password"
                showStrength={false}
                type={showPassword3 ? "text" : "password"}
                placeholder={t('ua_pw_old')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword3(p => !p)} edge="end">
                        {showPassword3 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={12}>
              <ButtonForm name={t('ua_pw_submit')} disabled={isSubmitting} />
            </Grid>

          </Grid>

          {message && (
            <Alert icon={<EmojiEmotionsIcon fontSize="inherit" />} variant="filled" severity="success" sx={{ mt: 4, borderRadius: '12px' }}>
              <AlertTitle>{t('ua_pw_success')}</AlertTitle>
            </Alert>
          )}

          {errmessage && (
            <Alert icon={<ReportProblemIcon fontSize="inherit" />} variant="filled" severity="warning" sx={{ mt: 2, borderRadius: '12px' }}>
              <AlertTitle>{t('ua_pw_error')}</AlertTitle>
            </Alert>
          )}

        </Form>
      )}
    </Formik>
  );
};

export default PwChangeForm;
